import { get, post, put, del } from '../client'
import {
  ProgrammeFilterParamsSchema,
  PaginationParamsSchema,
  CreateProgrammeSchema,
  UpdateProgrammeSchema,
  type Programme,
  type ProgrammeDetail,
  type ProgrammeFilterParams,
  type CreateProgramme,
  type UpdateProgramme,
  type ApiResponse,
  type PaginatedResponse,
  type PaginationParams
} from '../types'

export class ProgrammeService {

  async getProgrammes(
    params: PaginationParams = { page: 1, size: 10 },
    filters: ProgrammeFilterParams = {}
  ): Promise<ApiResponse<PaginatedResponse<Programme>>> {
    const validatedParams = PaginationParamsSchema.parse(params)
    const validatedFilters = ProgrammeFilterParamsSchema.parse(filters)

    const query = new URLSearchParams({
      page: validatedParams.page.toString(),
      size: validatedParams.size.toString()
    }).toString()

    return await post<ApiResponse<PaginatedResponse<Programme>>>(
      `programmes/private/filter?${query}`,
      validatedFilters  
    )
  }


  async createProgramme(data: CreateProgramme): Promise<ApiResponse<ProgrammeDetail>> {

    const validatedData = CreateProgrammeSchema.parse(data)


    const payload = {
      title: validatedData.title,
      coverImageTitle: validatedData.coverImageTitle,
      articleTag: validatedData.articleTag, // Array of strings
      landingPageAfsaProgramme: validatedData.landingPageAfsaProgramme,
      allowComments: validatedData.allowComments,
      content: validatedData.content,
      status: validatedData.status,
      coverImageUrl: validatedData.coverImageUrl // Image URL string - API expects this field name
    }

    return await post<ApiResponse<ProgrammeDetail>>(
      'programmes/admin/private',
      payload
    )
  }


  async updateProgramme(
    programmeId: string,
    data: UpdateProgramme
  ): Promise<ApiResponse<null>> {
    // Validate data
    const validatedData = UpdateProgrammeSchema.parse(data)

    // Use JSON body instead of FormData since we're sending URLs
    const payload = {
      title: validatedData.title,
      coverImageTitle: validatedData.coverImageTitle,
      articleTag: validatedData.articleTag, // Array of strings
      landingPageAfsaProgramme: validatedData.landingPageAfsaProgramme,
      allowComments: validatedData.allowComments,
      content: validatedData.content,
      ...(validatedData.status && { status: validatedData.status }),
      ...(validatedData.coverImageUrl && { coverImageUrl: validatedData.coverImageUrl })
    }

    return await put<ApiResponse<null>>(
      `programmes/admin/private/${programmeId}`,
      payload
    )
  }


  async updateProgrammeStatus(programmeId: string, status: 'DRAFT' | 'PUBLISH'): Promise<ApiResponse<Programme>> {
    return await put<ApiResponse<Programme>>(
      `programmes/admin/private/${programmeId}/status`,
      { status }
    )
  }


  async deleteProgramme(programmeId: string): Promise<ApiResponse<null>> {
    return await del<ApiResponse<null>>(
      `programmes/admin/private/${programmeId}`
    )
  }


  async getProgrammeById(programmeId: string): Promise<ApiResponse<ProgrammeDetail>> {
    return await get<ApiResponse<ProgrammeDetail>>(
      `programmes/private/${programmeId}`
    )
  }


  async getTotalPublishedProgrammes(): Promise<ApiResponse<{ publishedProgrammes: number }>> {
    return await get<ApiResponse<{ publishedProgrammes: number }>>(
      'programmes/private/count/published'
    )
  }


  async getTotalDraftProgrammes(): Promise<ApiResponse<{ draftProgrammes: number }>> {
    return await get<ApiResponse<{ draftProgrammes: number }>>(
      'programmes/private/count/draft'
    )
  }
}


export const programmeService = new ProgrammeService()

// Export default
export default programmeService
